import type { DocumentData } from "firebase/firestore";
import { compactData, dateValue, stringValue } from "./firestore";
import {
  activeFields,
  fieldDefinitionFirestoreData,
  parseFieldDefinitions,
  sbotSlug,
  type LeagueAthleteFieldDefinition,
} from "./fieldDefinition";
import {
  decodeDynamicValues,
  encodeDynamicValues,
  type LeagueDynamicFieldValue,
} from "./dynamicFieldValue";

// Ports of LeagueCompetitionTemplate / LeagueCompetitionInstance; field
// contracts per firestore.rules validCompetitionTemplateShape / InstanceShape.

export type LeagueCompetitionTemplateVisibility = "private" | "public";
export type LeagueCompetitionParticipantFormat = "individual" | "team" | "relay" | "other";
export type LeagueCompetitionResultFormat = "time" | "distance" | "height" | "points" | "score" | "placement" | "text";
export type LeagueCompetitionInstanceStatus = "draft" | "published" | "archived";

export const PARTICIPANT_FORMATS: LeagueCompetitionParticipantFormat[] = ["individual", "team", "relay", "other"];
export const RESULT_FORMATS: LeagueCompetitionResultFormat[] = [
  "time",
  "distance",
  "height",
  "points",
  "score",
  "placement",
  "text",
];
export const INSTANCE_STATUSES: LeagueCompetitionInstanceStatus[] = ["draft", "published", "archived"];

export interface LeagueCompetitionTemplate {
  id: string;
  leagueID: string;
  sportID: string;
  name: string;
  slug: string;
  description?: string;
  visibility: LeagueCompetitionTemplateVisibility;
  participantFormat: LeagueCompetitionParticipantFormat;
  resultFormat: LeagueCompetitionResultFormat;
  resultUnit?: string;
  version: number;
  fields: LeagueAthleteFieldDefinition[];
  sourceTemplateID?: string;
  sourceLeagueID?: string;
  createdByUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
  updatedByUserID?: string;
  isArchived: boolean;
}

export interface LeagueCompetitionInstance {
  id: string;
  leagueID: string;
  sportID: string;
  templateID: string;
  templateName: string;
  templateVersion: number;
  name: string;
  status: LeagueCompetitionInstanceStatus;
  publicValues: Record<string, LeagueDynamicFieldValue>;
  organizationValues: Record<string, LeagueDynamicFieldValue>;
  leagueValues: Record<string, LeagueDynamicFieldValue>;
  createdByUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
  updatedByUserID?: string;
}

export function parseCompetitionTemplate(id: string, data: DocumentData): LeagueCompetitionTemplate | null {
  const leagueID = stringValue(data.leagueID);
  const sportID = stringValue(data.sportID);
  const name = stringValue(data.name);
  const slug = stringValue(data.slug);
  if (!leagueID || !sportID || !name || !slug) return null;

  const visibility = data.visibility === "public" ? "public" : "private";
  const participantFormat = (PARTICIPANT_FORMATS as string[]).includes(data.participantFormat as string)
    ? (data.participantFormat as LeagueCompetitionParticipantFormat)
    : "individual";
  const resultFormat = (RESULT_FORMATS as string[]).includes(data.resultFormat as string)
    ? (data.resultFormat as LeagueCompetitionResultFormat)
    : "placement";

  return {
    id,
    leagueID,
    sportID,
    name,
    slug,
    description: stringValue(data.description),
    visibility,
    participantFormat,
    resultFormat,
    resultUnit: stringValue(data.resultUnit),
    version: typeof data.version === "number" ? data.version : 0,
    fields: parseFieldDefinitions(data.fields),
    sourceTemplateID: stringValue(data.sourceTemplateID),
    sourceLeagueID: stringValue(data.sourceLeagueID),
    createdByUserID: stringValue(data.createdByUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
    updatedByUserID: stringValue(data.updatedByUserID),
    isArchived: data.isArchived === true,
  };
}

export function competitionTemplateFirestoreData(template: LeagueCompetitionTemplate): Record<string, unknown> {
  return compactData({
    id: template.id,
    leagueID: template.leagueID,
    sportID: template.sportID,
    name: template.name,
    slug: template.slug,
    visibility: template.visibility,
    participantFormat: template.participantFormat,
    resultFormat: template.resultFormat,
    version: template.version,
    fields: [...template.fields].sort((a, b) => a.sortOrder - b.sortOrder).map(fieldDefinitionFirestoreData),
    isArchived: template.isArchived,
    description: template.description,
    resultUnit: template.resultUnit,
    sourceTemplateID: template.sourceTemplateID,
    sourceLeagueID: template.sourceLeagueID,
    createdByUserID: template.createdByUserID,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
    updatedByUserID: template.updatedByUserID,
  });
}

export function templateActiveFields(template: LeagueCompetitionTemplate): LeagueAthleteFieldDefinition[] {
  return activeFields(template.fields);
}

export function parseCompetitionInstance(id: string, data: DocumentData): LeagueCompetitionInstance | null {
  const storedID = stringValue(data.id);
  const leagueID = stringValue(data.leagueID);
  const sportID = stringValue(data.sportID);
  const templateID = stringValue(data.templateID);
  const templateName = stringValue(data.templateName);
  const name = stringValue(data.name);
  const status = data.status;
  if (
    storedID !== id ||
    !leagueID ||
    !sportID ||
    !templateID ||
    !templateName ||
    typeof data.templateVersion !== "number" ||
    !name ||
    (status !== "draft" && status !== "published" && status !== "archived")
  ) {
    return null;
  }

  return {
    id,
    leagueID,
    sportID,
    templateID,
    templateName,
    templateVersion: data.templateVersion,
    name,
    status,
    publicValues: decodeDynamicValues(data.publicValues),
    organizationValues: decodeDynamicValues(data.organizationValues),
    leagueValues: decodeDynamicValues(data.leagueValues),
    createdByUserID: stringValue(data.createdByUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
    updatedByUserID: stringValue(data.updatedByUserID),
  };
}

export function competitionInstanceFirestoreData(instance: LeagueCompetitionInstance): Record<string, unknown> {
  return compactData({
    id: instance.id,
    leagueID: instance.leagueID,
    sportID: instance.sportID,
    templateID: instance.templateID,
    templateName: instance.templateName,
    templateVersion: instance.templateVersion,
    name: instance.name,
    status: instance.status,
    publicValues: encodeDynamicValues(instance.publicValues),
    organizationValues: encodeDynamicValues(instance.organizationValues),
    leagueValues: encodeDynamicValues(instance.leagueValues),
    createdByUserID: instance.createdByUserID,
    createdAt: instance.createdAt,
    updatedAt: instance.updatedAt,
    updatedByUserID: instance.updatedByUserID,
  });
}

/** Merge precedence mirrors LeagueCompetitionInstance.allValues (public wins, then org, then league). */
export function instanceAllValues(instance: LeagueCompetitionInstance): Record<string, LeagueDynamicFieldValue> {
  return { ...instance.leagueValues, ...instance.organizationValues, ...instance.publicValues };
}

export function makeInstanceID(leagueID: string, seed?: string): string {
  const suffix = sbotSlug(seed ?? crypto.randomUUID());
  return `${leagueID}_competition_${suffix}`;
}
